import React, { useCallback, useMemo } from "react";
import { Rnd } from "react-rnd";
import { Button } from "../ui";
import {
  PosterWidgetBracket,
  PosterWidgetGroups,
  PosterWidgetMatches,
  PosterWidgetSchedule,
} from "./PosterDataWidgets";
import { newBlockId } from "./posterCanvasDefaults";
import {
  ALIGN_VALUES,
  FONT_FAMILIES,
  TEXT_FONT_SIZE_MAX,
  TEXT_FONT_SIZE_MIN,
  TEXT_MAX_LENGTH,
  defaultTextBlock,
  defaultWidgetOptions,
  fontFamilyCss,
  resolveTextBlock,
} from "./posterWidgetOptions";
import { buildQrPreviewUrl } from "./premiumMediaUtils";
import WidgetOptionsInspector from "./WidgetOptionsInspector";

const dragHandleClass = "poster-block-drag-handle";

function sortedBlocks(blocks) {
  return [...(blocks || [])].sort((a, b) => (a.z || 0) - (b.z || 0));
}

/**
 * Returns the columns the user can reorder for a given widget kind. Each entry
 * is `{ id, label }`; `id` is the key persisted in widgetOptions.columnsOrder,
 * `label` is what the inspector shows. Deliberately tolerant of missing data
 * so the inspector never crashes during a partial load.
 */
function computeAvailableColumns(kind, options, data) {
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const matches = Array.isArray(data?.matches) ? data.matches : [];
  const bracket = Array.isArray(data?.bracket) ? data.bracket : [];

  if (kind === "groups") {
    return groups.map((g, i) => ({
      id: String(g?.id ?? g?.Id ?? i),
      label: String(g?.name ?? g?.Name ?? `Grupo ${i + 1}`),
    }));
  }

  if (kind === "schedule" || kind === "matches") {
    const seen = new Set();
    const out = [];
    for (const m of matches) {
      const raw = String(m?.fieldName ?? m?.FieldName ?? "").trim() || "—";
      if (seen.has(raw)) continue;
      seen.add(raw);
      out.push({ id: raw, label: raw });
    }
    return out;
  }

  // Bracket: the "list"/"rounds" layouts group by round (depth derived from
  // NextMatchId), whose natural order "deepest stage first → final" should not
  // be reordered. The "fields" layout has Campo columns, which the user can
  // reorder just like the schedule/matches widgets.
  if (kind === "bracket") {
    if ((options?.bracketLayout ?? "rounds") !== "fields") return [];
    const seen = new Set();
    const out = [];
    for (const m of bracket) {
      const raw = String(m?.fieldName ?? m?.FieldName ?? "").trim() || "—";
      if (seen.has(raw)) continue;
      seen.add(raw);
      out.push({ id: raw, label: raw });
    }
    return out;
  }

  return [];
}

const TEXT_ALIGN_OPTIONS = [
  { id: "left", label: "Izquierda" },
  { id: "center", label: "Centro" },
  { id: "right", label: "Derecha" },
];

/**
 * Inspector for a free-form text block: content + typography + colours. Patches
 * the block fields directly (text/fontFamily/fontSize/color/bgColor/align/bold/italic).
 */
function TextBlockInspector({ block, onPatch }) {
  const t = resolveTextBlock(block);
  const labelClass = "flex items-center gap-1 text-xs font-semibold text-neutral-700";

  return (
    <div className="flex flex-col gap-2 rounded border border-neutral-300 bg-neutral-50 px-3 py-2">
      <textarea
        className="w-full resize-y rounded border border-neutral-400 bg-white px-2 py-1.5 text-sm"
        rows={2}
        maxLength={TEXT_MAX_LENGTH}
        placeholder="Escribe el texto…"
        value={t.text}
        onChange={(e) => onPatch({ text: e.target.value })}
      />
      <div className="flex flex-wrap items-center gap-3">
        <label className={labelClass}>
          Fuente
          <select
            className="rounded border border-neutral-400 bg-white px-1.5 py-0.5 text-xs"
            value={t.fontFamily}
            onChange={(e) => onPatch({ fontFamily: e.target.value })}
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Tamaño
          <input
            type="number"
            className="w-16 rounded border border-neutral-400 bg-white px-1.5 py-0.5 text-xs"
            min={TEXT_FONT_SIZE_MIN}
            max={TEXT_FONT_SIZE_MAX}
            value={t.fontSize}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) {
                onPatch({ fontSize: Math.max(TEXT_FONT_SIZE_MIN, Math.min(TEXT_FONT_SIZE_MAX, Math.round(n))) });
              }
            }}
          />
        </label>
        <label className={labelClass}>
          Alineación
          <select
            className="rounded border border-neutral-400 bg-white px-1.5 py-0.5 text-xs"
            value={ALIGN_VALUES.includes(t.align) ? t.align : "left"}
            onChange={(e) => onPatch({ align: e.target.value })}
          >
            {TEXT_ALIGN_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className={labelClass}>
          Color
          <input
            type="color"
            className="h-6 w-8 cursor-pointer rounded border border-neutral-400 bg-white"
            value={t.color}
            onChange={(e) => onPatch({ color: e.target.value })}
          />
        </label>
        <label className={labelClass}>
          <input
            type="checkbox"
            className="h-3.5 w-3.5"
            checked={!!t.bgColor}
            onChange={(e) => onPatch({ bgColor: e.target.checked ? "#ffffff" : "" })}
          />
          Fondo
        </label>
        {t.bgColor ? (
          <input
            type="color"
            className="h-6 w-8 cursor-pointer rounded border border-neutral-400 bg-white"
            value={t.bgColor}
            onChange={(e) => onPatch({ bgColor: e.target.value })}
            aria-label="Color de fondo"
          />
        ) : null}
        <label className={labelClass}>
          <input
            type="checkbox"
            className="h-3.5 w-3.5"
            checked={!!t.bold}
            onChange={(e) => onPatch({ bold: e.target.checked })}
          />
          Negrita
        </label>
        <label className={labelClass}>
          <input
            type="checkbox"
            className="h-3.5 w-3.5"
            checked={!!t.italic}
            onChange={(e) => onPatch({ italic: e.target.checked })}
          />
          Cursiva
        </label>
      </div>
    </div>
  );
}

function BlockChrome({ title, interactive }) {
  if (!interactive) return null;
  return (
    <div
      className={`${dragHandleClass} flex min-h-[28px] cursor-grab items-center justify-between gap-2 border-b border-neutral-400 bg-neutral-200 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-neutral-800 active:cursor-grabbing`}
    >
      <span className="truncate">{title}</span>
      <span className="shrink-0 text-neutral-600">⋮⋮</span>
    </div>
  );
}

export default function PosterCanvasEditor({
  layout,
  onLayoutChange,
  tournamentId,
  posterTitle,
  posterSubtitle,
  sponsors,
  qrPreviewUrl,
  publicUrl,
  qrSize,
  canUseQr,
  canUseSponsors,
  interactive = true,
  selectedBlockId,
  onSelectBlock,
  onUserHint,
  posterData,
}) {
  const groups = posterData?.groups ?? [];
  const matches = posterData?.matches ?? [];
  const bracket = posterData?.bracket ?? [];
  const blocks = layout?.blocks || [];

  const selected = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  const patchBlock = useCallback(
    (id, partial) => {
      onLayoutChange({
        ...layout,
        blocks: blocks.map((b) => (b.id === id ? { ...b, ...partial } : b)),
      });
    },
    [layout, blocks, onLayoutChange]
  );

  const removeBlock = useCallback(
    (id) => {
      onLayoutChange({
        ...layout,
        blocks: blocks.filter((b) => b.id !== id),
      });
      if (selectedBlockId === id) onSelectBlock?.(null);
    },
    [layout, blocks, onLayoutChange, onSelectBlock, selectedBlockId]
  );

  const bringForward = useCallback(
    (id) => {
      const maxZ = Math.max(0, ...blocks.map((b) => b.z || 0));
      patchBlock(id, { z: maxZ + 1 });
    },
    [blocks, patchBlock]
  );

  const addBlock = useCallback(
    (type, extra = {}) => {
      const maxZ = Math.max(0, ...blocks.map((b) => b.z || 0));
      const id = newBlockId();
      const base = { id, type, x: 48, y: 80 + blocks.length * 24, z: maxZ + 1 };

      if (type === "header") {
        onLayoutChange({
          ...layout,
          blocks: [...blocks, { ...base, w: 1040, h: 96, ...extra }],
        });
        onSelectBlock?.(id);
        return;
      }
      if (type === "text") {
        onLayoutChange({
          ...layout,
          blocks: [...blocks, { ...base, w: 460, h: 90, ...defaultTextBlock(), ...extra }],
        });
        onSelectBlock?.(id);
        return;
      }
      if (type === "widget") {
        const newKind = extra.widgetKind || "groups";
        // Default orientation: horizontal (lado a lado) for kinds that support
        // flow, since cartels are usually wide. Schedule is a matrix and ignores
        // flow, but we still set it for consistency.
        const newFlow = "horizontal";
        onLayoutChange({
          ...layout,
          blocks: [
            ...blocks,
            {
              ...base,
              w: 380,
              h: 420,
              widgetKind: newKind,
              widgetFlow: newFlow,
              widgetOptions: defaultWidgetOptions(newKind),
            },
          ],
        });
        onSelectBlock?.(id);
        return;
      }
      if (type === "qr") {
        if (!canUseQr) {
          onUserHint?.("Tu plan no incluye QR en el cartel.");
          return;
        }
        onLayoutChange({
          ...layout,
          blocks: [...blocks, { ...base, w: 200, h: 200 }],
        });
        onSelectBlock?.(id);
        return;
      }
      if (type === "sponsor") {
        if (!canUseSponsors) {
          onUserHint?.("Tu plan no incluye imagenes de patrocinio.");
          return;
        }
        if (!sponsors.length) {
          onUserHint?.("Sube primero imagenes en Patrocinadores.");
          return;
        }
        const sponsorIndex = Math.min(extra.sponsorIndex ?? sponsors.length - 1, sponsors.length - 1);
        onLayoutChange({
          ...layout,
          blocks: [...blocks, { ...base, w: 280, h: 120, sponsorIndex }],
        });
        onSelectBlock?.(id);
      }
    },
    [blocks, layout, onLayoutChange, onSelectBlock, canUseQr, canUseSponsors, sponsors, onUserHint]
  );

  const setCanvasSize = useCallback(
    (w, h) => {
      onLayoutChange({
        ...layout,
        w: Math.round(w),
        h: Math.round(h),
      });
    },
    [layout, onLayoutChange]
  );

  const widgetKind = (block) => block.widgetKind || block.iframeMode || "groups";

  const widgetFlow = (block) => (block.widgetFlow === "horizontal" ? "horizontal" : "vertical");

  const renderBlockContent = (block) => {
    if (block.type === "header") {
      return (
        <div className="flex h-full flex-col justify-center overflow-hidden px-3 py-1">
          <h2 className="font-display text-[clamp(1rem,2.5vw,1.75rem)] font-extrabold leading-tight text-neutral-900">
            {posterTitle || "Cartel del torneo"}
          </h2>
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-neutral-600">
            {posterSubtitle || "Subtitulo del cartel"}
          </p>
        </div>
      );
    }
    if (block.type === "widget" || block.type === "iframe") {
      const kind = widgetKind(block);
      const flow = widgetFlow(block);
      const options = block.widgetOptions;
      if (kind === "matches") {
        return <PosterWidgetMatches matches={matches} groups={groups} flow={flow} options={options} />;
      }
      if (kind === "bracket") {
        return <PosterWidgetBracket bracket={bracket} groups={groups} flow={flow} options={options} />;
      }
      if (kind === "schedule") {
        return <PosterWidgetSchedule matches={matches} groups={groups} options={options} />;
      }
      return <PosterWidgetGroups groups={groups} flow={flow} options={options} />;
    }
    if (block.type === "text") {
      const t = resolveTextBlock(block);
      return (
        <div
          className="flex h-full w-full overflow-hidden px-2 py-1"
          style={{ alignItems: "center", backgroundColor: t.bgColor || "transparent" }}
        >
          <span
            className="block w-full whitespace-pre-wrap break-words"
            style={{
              fontFamily: fontFamilyCss(t.fontFamily),
              fontSize: t.fontSize,
              lineHeight: 1.2,
              color: t.color,
              textAlign: t.align,
              fontWeight: t.bold ? 700 : 400,
              fontStyle: t.italic ? "italic" : "normal",
            }}
          >
            {t.text || "Texto"}
          </span>
        </div>
      );
    }
    if (block.type === "qr") {
      const qrUrl = canUseQr
        ? buildQrPreviewUrl(publicUrl, qrSize, { color: block.fgColor, bgColor: block.bgColor }) || qrPreviewUrl
        : "";
      if (!qrUrl) {
        return (
          <div className="flex h-full items-center justify-center bg-neutral-100 p-2 text-center text-[10px] text-neutral-500">
            QR no disponible
          </div>
        );
      }
      return (
        <div
          className="flex h-full w-full items-center justify-center p-1"
          style={{ backgroundColor: block.bgColor && block.bgColor !== "transparent" ? block.bgColor : "#ffffff" }}
        >
          <img
            src={qrUrl}
            alt="QR"
            crossOrigin="anonymous"
            className="max-h-full max-w-full object-contain"
          />
        </div>
      );
    }
    if (block.type === "sponsor") {
      const src = sponsors[block.sponsorIndex];
      if (!src) {
        return (
          <div className="flex h-full items-center justify-center bg-neutral-100 text-[10px] text-neutral-500">
            Sin imagen
          </div>
        );
      }
      return <img src={src} alt="Patrocinio" className="h-full w-full object-contain p-1" />;
    }
    return null;
  };

  const blockTitle = (block) => {
    if (block.type === "header") return "Cabecera";
    if (block.type === "text") return "Texto";
    if (block.type === "widget" || block.type === "iframe") {
      const k = widgetKind(block);
      if (k === "groups") return "Grupos";
      if (k === "matches") return "Partidos";
      if (k === "schedule") return "Cuadro horario";
      return "Bracket";
    }
    if (block.type === "qr") return "QR";
    if (block.type === "sponsor") return `Publicidad ${(block.sponsorIndex ?? 0) + 1}`;
    return block.type;
  };

  const renderOneBlock = (block) => {
    const isSelected = block.id === selectedBlockId;
    const title = blockTitle(block);

    const inner = (
      <div className="flex h-full w-full flex-col overflow-hidden border border-neutral-400 bg-white">
        <BlockChrome title={title} interactive={interactive} />
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{renderBlockContent(block)}</div>
      </div>
    );

    if (!interactive) {
      return (
        <div
          key={block.id}
          className="absolute box-border overflow-hidden bg-white print:border-neutral-400"
          style={{
            left: block.x,
            top: block.y,
            width: block.w,
            height: block.h,
            zIndex: block.z || 1,
          }}
        >
          {inner}
        </div>
      );
    }

    return (
      <Rnd
        key={block.id}
        size={{ width: block.w, height: block.h }}
        position={{ x: block.x, y: block.y }}
        onDragStart={() => onSelectBlock?.(block.id)}
        onResizeStart={() => onSelectBlock?.(block.id)}
        onDragStop={(e, d) => patchBlock(block.id, { x: d.x, y: d.y })}
        onResizeStop={(e, dir, ref, delta, pos) => {
          patchBlock(block.id, {
            w: ref.offsetWidth,
            h: ref.offsetHeight,
            x: pos.x,
            y: pos.y,
          });
        }}
        minWidth={120}
        minHeight={80}
        bounds="parent"
        dragHandleClassName={dragHandleClass}
        enableResizing={{
          top: true,
          right: true,
          bottom: true,
          left: true,
          topRight: true,
          bottomRight: true,
          bottomLeft: true,
          topLeft: true,
        }}
        className={`poster-rnd-box box-border ${isSelected ? "ring-2 ring-amber-500 ring-offset-1" : ""} print:hidden`}
        style={{ zIndex: block.z || 1 }}
      >
        {inner}
      </Rnd>
    );
  };

  return (
    <div className="poster-canvas-editor flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {interactive ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-neutral-600">Anadir bloque:</span>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("header")}>
            Cabecera
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("text")}>
            Texto
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("widget", { widgetKind: "groups" })}>
            Grupos
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("widget", { widgetKind: "matches" })}>
            Partidos
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("widget", { widgetKind: "bracket" })}>
            Bracket
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("widget", { widgetKind: "schedule" })}>
            Cuadro horario
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("qr")}>
            QR
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => addBlock("sponsor")}>
            Banner / logo
          </Button>
        </div>
      ) : null}

      {interactive && selected ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm">
          <span className="font-bold text-neutral-700">Bloque:</span>
          {selected.type === "widget" || selected.type === "iframe" ? (
            <>
              <select
                className="rounded border border-neutral-400 bg-white px-2 py-1 text-sm"
                value={widgetKind(selected)}
                onChange={(e) => {
                  const v = e.target.value;
                  onLayoutChange({
                    ...layout,
                    blocks: blocks.map((b) => {
                      if (b.id !== selected.id) return b;
                      const next = {
                        ...b,
                        type: "widget",
                        widgetKind: v,
                        widgetOptions: defaultWidgetOptions(v),
                      };
                      delete next.iframeMode;
                      return next;
                    }),
                  });
                }}
              >
                <option value="groups">Grupos</option>
                <option value="matches">Partidos</option>
                <option value="bracket">Bracket</option>
                <option value="schedule">Cuadro horario</option>
              </select>
              {widgetKind(selected) !== "schedule" ? (
                <label className="flex items-center gap-1.5 text-xs text-neutral-700">
                  <span className="font-semibold">Disposicion</span>
                  <select
                    className="rounded border border-neutral-400 bg-white px-2 py-1 text-sm"
                    value={widgetFlow(selected)}
                    onChange={(e) => patchBlock(selected.id, { widgetFlow: e.target.value })}
                  >
                    <option value="vertical">Vertical (lista / tabla)</option>
                    <option value="horizontal">Horizontal (lado a lado)</option>
                  </select>
                </label>
              ) : null}
            </>
          ) : null}
          {selected.type === "sponsor" && sponsors.length > 0 ? (
            <select
              className="rounded border border-neutral-400 bg-white px-2 py-1 text-sm"
              value={selected.sponsorIndex ?? 0}
              onChange={(e) => patchBlock(selected.id, { sponsorIndex: Number(e.target.value) })}
            >
              {sponsors.map((_, i) => (
                <option key={i} value={i}>
                  Imagen {i + 1}
                </option>
              ))}
            </select>
          ) : null}
          {selected.type === "qr" && canUseQr ? (
            <>
              <label className="flex items-center gap-1 text-xs font-semibold text-neutral-700">
                Color QR
                <input
                  type="color"
                  className="h-6 w-8 cursor-pointer rounded border border-neutral-400 bg-white"
                  value={selected.fgColor || "#000000"}
                  onChange={(e) => patchBlock(selected.id, { fgColor: e.target.value })}
                />
              </label>
              <label className="flex items-center gap-1 text-xs font-semibold text-neutral-700">
                Fondo
                <input
                  type="color"
                  className="h-6 w-8 cursor-pointer rounded border border-neutral-400 bg-white"
                  value={selected.bgColor || "#ffffff"}
                  onChange={(e) => patchBlock(selected.id, { bgColor: e.target.value })}
                />
              </label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => patchBlock(selected.id, { fgColor: undefined, bgColor: undefined })}
              >
                Color por defecto
              </Button>
            </>
          ) : null}
          <Button type="button" size="sm" variant="outline" onClick={() => bringForward(selected.id)}>
            Traer al frente
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => removeBlock(selected.id)}>
            Eliminar
          </Button>
        </div>
      ) : null}

      {interactive && selected && selected.type === "text" ? (
        <TextBlockInspector
          block={selected}
          onPatch={(partial) => patchBlock(selected.id, partial)}
        />
      ) : null}

      {interactive && selected && (selected.type === "widget" || selected.type === "iframe") ? (
        <WidgetOptionsInspector
          kind={widgetKind(selected)}
          options={selected.widgetOptions}
          onPatch={(nextOpts) => patchBlock(selected.id, { widgetOptions: nextOpts })}
          availableColumns={computeAvailableColumns(widgetKind(selected), selected.widgetOptions, { groups, matches, bracket })}
        />
      ) : null}

      {interactive ? (
        <div className="flex flex-wrap gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-neutral-600">Ancho lienzo</span>
            <input
              type="number"
              className="w-24 rounded border border-neutral-300 px-2 py-1"
              min={320}
              max={2400}
              value={layout.w}
              onChange={(e) => setCanvasSize(Number(e.target.value) || layout.w, layout.h)}
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-neutral-600">Alto lienzo</span>
            <input
              type="number"
              className="w-24 rounded border border-neutral-300 px-2 py-1"
              min={400}
              max={8000}
              value={layout.h}
              onChange={(e) => setCanvasSize(layout.w, Number(e.target.value) || layout.h)}
            />
          </label>
        </div>
      ) : null}

      <style>{`
        .poster-canvas-editor .react-resizable-handle {
          width: 14px !important;
          height: 14px !important;
          background: #1f2937 !important;
          border: 2px solid #fff !important;
          box-shadow: 0 0 0 1px rgba(0,0,0,0.2);
        }
        .poster-canvas-editor .react-resizable-handle-se {
          right: -6px !important;
          bottom: -6px !important;
        }
        .poster-canvas-editor .react-resizable-handle-sw { left: -6px !important; bottom: -6px !important; }
        .poster-canvas-editor .react-resizable-handle-ne { right: -6px !important; top: -6px !important; }
        .poster-canvas-editor .react-resizable-handle-nw { left: -6px !important; top: -6px !important; }
        .poster-canvas-editor .react-resizable-handle-n,
        .poster-canvas-editor .react-resizable-handle-s {
          width: 20px !important;
          height: 10px !important;
          left: 50% !important;
          margin-left: -10px !important;
        }
        .poster-canvas-editor .react-resizable-handle-e,
        .poster-canvas-editor .react-resizable-handle-w {
          width: 10px !important;
          height: 20px !important;
          top: 50% !important;
          margin-top: -10px !important;
        }
        @media print {
          .poster-canvas-editor .react-resizable-handle,
          .poster-canvas-editor .${dragHandleClass} { display: none !important; }
        }
      `}</style>

      <div className="min-h-0 min-w-0 flex-1 overflow-auto rounded-lg border border-neutral-300 bg-neutral-200/80 p-4 pb-10 pr-10">
        <div
          className="poster-canvas-rnd relative inline-block bg-neutral-300/50"
          style={{ width: layout.w, height: layout.h }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onSelectBlock?.(null);
          }}
        >
          <div
            id="poster-pdf-capture-root"
            className="relative bg-white"
            style={{ width: layout.w, height: layout.h }}
          >
            {sortedBlocks(blocks).map((b) => renderOneBlock(b))}
          </div>
        </div>
      </div>

      {interactive ? (
        <p className="text-xs text-neutral-600">
          Disposicion: vertical = grupos o bloques de campo uno debajo de otro; horizontal = grupos en columnas (A | B |
          …) y partidos/bracket en columnas por campo. Si hay muchos grupos o campos, usa scroll horizontal dentro del
          bloque o ensancha el bloque.
        </p>
      ) : null}
    </div>
  );
}
